import {UserContext} from './subject';
import {Injectable, signal} from '@angular/core';
import {AppRoleAndPermissions} from './app-role-and-permissions';
import {Portfolio, Segment, SegmentStatusEnum, UserProfileData} from '../../api-client-kt/src';
import {AppConstants} from '../app.constants';
import {AccountDataWithChecks, CellComment} from '../data/app-data-checks';
import {LoadedPolicy, loadPolicy} from '@open-policy-agent/opa-wasm';
import {TTLCache} from '@isaacs/ttlcache';
import {PortfolioSegmentDataContext} from '../data/portfolio-segment-data-context.service';
import {AppPerformanceTrackers} from '../lib/performance';
import {SegmentWithPermissions} from '../data/portfolio-data-context.service';
import {StringUtils} from '../lib/strings';

export interface EvaluationResult {
    allowed: boolean;
    reason?: string;
}

const HAS_ACCESS: EvaluationResult = { allowed: true };
const HAS_NO_ACCESS = { allowed: false, reason: "Access denied by policy" };

const AnonymousUserContext: UserContext = {
    userId: AppConstants.NULL_UUID,
    userName: 'anonymous',
    roles: [],
    lobOfficePermissions: []
}

interface SegmentLevelContext {
    user: UserContext;
    portfolio: Portfolio;
    segment: Segment;
}

interface AccountLevelContext extends SegmentLevelContext{
    account: AccountDataWithChecks;
}

const AccountPricedDataAttributes = [
    "renewalAptp", "renewalShare", "renewalExposure", "renewalDeductible", "renewalLimit",
    "repricingFacilityErc", "manualErc", "manualComments", "manualOverride"
];
const AccountRarcAttributes = [
    "renewalGwp", "renewalGnwp", "renewalCurrency", "renewalGwpEur", "renewalGnwpEur",
    "renewalInceptionDate", "renewalExpiryDate"
];

@Injectable({
    providedIn: 'root'
})
export class AccessControlService {

    readonly userContext = signal<UserContext>(AnonymousUserContext);

    static AppRoles = [
        { name: 'Developer and Operations', key: 'devops' },
        { name: 'Central Team TechEx', key: 'ct_techex' },
        { name: 'Central Team Global Head of Lob', key: 'ct_glh' },
        { name: 'Central Team Underwriter', key: 'ct_uw' },
        { name: 'Local TechEx', key: 'local_techex' },
        { name: 'Local Head of Lob', key: 'local_lh' },
        { name: 'Local Underwriter', key: 'local_uw' },
    ];

    private appPolicy!: LoadedPolicy;

    // OPA must get cached, because it delegates the evaluation to the WASM engine.
    // The WASM engine is not slow, but it stops the UI thread from handling events
    // Especially, when there are a lot of concurrent calls (Wijmo Grid, scrolling up by keyboard!)
    // we need to avoid blocking the UI thread.
    // The cache is bound by max 10k entries, and entries expire after 1s.
    // TTL of 1000ms: must be a small amount to prevent outdated results from being cached
    private opaCache = new TTLCache<string, any>({
        max: 10_000,
        ttl: 100
    });

    constructor() {
        this.loadPolicies()
            .then(() => console.log("Policies loaded successfully"))
            .catch(error => console.error(error));
    }

    async loadPolicies() {
        const policyRequest = await fetch('/opa/pfm_app.wasm')
        this.appPolicy = await loadPolicy(await policyRequest.arrayBuffer());
    }

    private opaCachedEvaluation(cacheKey: string, policy: LoadedPolicy, input: any, entryPoint: string): any {
        const opaCacheKey = `${entryPoint}:${cacheKey}`;
        let result = this.opaCache.get(opaCacheKey);
        if (result == undefined) {
            AppPerformanceTrackers.OpaEvaluation.measure(() => {
                result = policy.evaluate(input, entryPoint);
                this.opaCache.set(opaCacheKey, result);
            });
        } else {
            AppPerformanceTrackers.OpaCacheHits.reportMeasurement();
        }
        return result;
    }

    public isSuperuser(): boolean {
        return this.userContext().roles.includes('devops');
    }

    public isCentralTeam(): boolean {
        return this.userContext().roles.some(role => role.startsWith('ct_'));
    }

    public isLocalTeam(): boolean {
        return this.userContext().roles.some(role => role.startsWith('local_'));
    }

    public isTechEx(): boolean {
        return this.userContext().roles.some(role => role.endsWith('_techex'));
    }

    public canAccessSegment(segment: Segment): EvaluationResult {
        // Then evaluate using the precompiled evaluator
        const user = this.userContext();
        const officeKey = segment.officeKey;
        const lobKey = segment.lobKey;
        const isAllowed = user.lobOfficePermissions.some(p => p.officeKey === officeKey && p.lobKey === lobKey && p.allow);
        return isAllowed ? HAS_ACCESS : HAS_NO_ACCESS;
    }

    private userHasBasicAccess(user: UserContext, account: any): boolean {
        return !(!user || !user.roles || user.roles.length === 0);
    }

    public applyRolePermissions(rolePermissions: AppRoleAndPermissions | null) {
        const rolesAndPermissions = rolePermissions?.toSubjectRolesAndPermissions() ?? {roles: [], lobOfficePermissions: []};
        const roles = rolesAndPermissions.roles ?? [];
        const userContext: UserContext = { ... this.userContext(), roles, lobOfficePermissions: rolesAndPermissions.lobOfficePermissions };
        this.userContext.set(userContext);
    }

    initFromProfile(myProfile: UserProfileData) {
        const userContext: UserContext = {
            userId: myProfile.userId ?? AnonymousUserContext.userId,
            userName: myProfile.userName ?? 'anonymous',
            roles: Array.from(myProfile.roles ?? new Set()),
            lobOfficePermissions: Array.from(myProfile.permissions ?? new Set())
        };
        this.userContext.set(userContext);
    }

    hasRole(attribute: string) {
        return this.userContext().roles.includes(attribute);
    }

    canEditSegment(segment: Segment, portfolio: Portfolio): EvaluationResult {
        const cacheKey = `${segment.id}-${segment.status}`;
        const ctx: SegmentLevelContext = { user: this.userContext(), segment, portfolio };
        const evaluated = this.opaCachedEvaluation(cacheKey, this.appPolicy, ctx, "pfm/segment/edit") as any;
        const result = evaluated[0].result as boolean;
        return result ? HAS_ACCESS : HAS_NO_ACCESS;
    }

    canEditSegmentCtx(pfSegDataCtx: PortfolioSegmentDataContext): EvaluationResult {
        const segment = pfSegDataCtx.activeSegment.value()
        if (segment == null) return HAS_NO_ACCESS;
        else return this.canEditSegment(segment, pfSegDataCtx.portfolio.value()!);
    }

    canEditSegmentStatus(segment: Segment | undefined, portfolio: Portfolio | undefined): EvaluationResult {
        if (segment == undefined || portfolio == undefined) return HAS_NO_ACCESS;

        const cacheKey = `${segment.id}-${segment.status}`;
        const ctx: SegmentLevelContext = { user: this.userContext(), segment, portfolio };
        const evaluated = this.opaCachedEvaluation(cacheKey, this.appPolicy, ctx, "pfm/segment/edit_status") as any;
        const result = evaluated[0].result as boolean;
        return result ? HAS_ACCESS : HAS_NO_ACCESS;
    }

    canEditSomeAccount(segment: Segment, portfolio: Portfolio): EvaluationResult {
        if (segment == undefined || portfolio == undefined) return HAS_NO_ACCESS;

        const cacheKey = `${segment.id}-${segment.status}`;
        const ctx: SegmentLevelContext = { user: this.userContext(), segment, portfolio };
        const evaluated = this.opaCachedEvaluation(cacheKey, this.appPolicy, ctx, "pfm/segment/edit_accounts") as any;
        const result = evaluated[0].result as boolean;
        return result ? HAS_ACCESS : HAS_NO_ACCESS;
    }

    canEditAccount(account: AccountDataWithChecks, segment: Segment, portfolio: Portfolio): EvaluationResult {
        const cacheKey = account.id ?? '';
        const ctx: AccountLevelContext = {user: this.userContext(), account, segment, portfolio};
        const evaluated = this.opaCachedEvaluation(cacheKey, this.appPolicy, ctx, "pfm/account/edit");
        const evaluationResult = evaluated[0].result as boolean;
        return evaluationResult ? {allowed: true} : HAS_NO_ACCESS;
    }

    canEditAccountAttribute(account: AccountDataWithChecks, attribute: string, segment: Segment, portfolio: Portfolio): EvaluationResult {
        return this.canEditAccount(account, segment, portfolio);
    }

    /**
     * Determines if an account attribute can be edited, considering the server's pricing information and various user roles.
     * This is however not as easy as it seems, as the attribute restrictions can be complex and depend on multiple factors.
     * We have failed in our first conceptual logic and postpone this approach for later. It's currently NOT used.
     */
    private canEditAccountAttributeConsideringServerPricingInformation(account: AccountDataWithChecks, attribute: string, segment: Segment, portfolio: Portfolio): EvaluationResult {
        // super user can do anything
        if (this.isSuperuser()) return HAS_ACCESS;

        // once approved, no editing allowed
        if (segment.status === SegmentStatusEnum.Approved) return HAS_NO_ACCESS;

        // seems a bit pointless in the access control matrix but we keep it for now
        const isAccountRarc = AccountRarcAttributes.includes(attribute);

        // Pricing data cannot be modified manually, only via the pricing tool
        const hasPricingData = account.renewalAptp != undefined;
        const isPricedData = AccountPricedDataAttributes.includes(attribute);
        if (hasPricingData && isPricedData) return HAS_NO_ACCESS;

        if (segment.status === SegmentStatusEnum.InGlobalReview) {
            if (this.isCentralTeam()) return HAS_ACCESS;
        } else if (segment.status === SegmentStatusEnum.InLocalReview) {
            if (this.isTechEx()) return HAS_ACCESS;
        } else if (segment.status === SegmentStatusEnum.Open) {
            if (this.isLocalTeam() || this.isTechEx()) return HAS_ACCESS;
        }

        return HAS_NO_ACCESS;
    }

    canEditAccountStatus(account: AccountDataWithChecks, segment: Segment, portfolio: Portfolio): EvaluationResult {
        // super user can do anything
        if (this.isSuperuser()) return HAS_ACCESS;

        const cacheKey = account.id ?? '';
        const ctx: AccountLevelContext = { user: this.userContext(), account, segment, portfolio };
        const evaluated = this.opaCachedEvaluation(cacheKey, this.appPolicy, ctx, "pfm/account/edit_status") as any;
        const result = evaluated[0].result as boolean;
        return result ? HAS_ACCESS : HAS_NO_ACCESS;
    }

    canAddComment(segment: SegmentWithPermissions, account: AccountDataWithChecks) {
        if (this.isSuperuser()) return true;
        if (segment == undefined) return false;
        return segment.status !== SegmentStatusEnum.Approved;
    }

    canDeleteComment(segment: SegmentWithPermissions, account: AccountDataWithChecks, comment: CellComment) {
        if (this.isSuperuser()) return true;
        if (segment.status === SegmentStatusEnum.Approved) return false;
        if (StringUtils.equalsIgnoreCase(comment.createdBy, this.userContext().userName)) return true;

        if (segment.status === SegmentStatusEnum.Open) {
            if (this.isTechEx()) {
                return this.isCentralTeam();
            } else {
                return this.isLocalTeam();
            }
        } else if (segment.status === SegmentStatusEnum.InLocalReview) {
            return this.isTechEx();
        } else if (segment.status === SegmentStatusEnum.InGlobalReview) {
            return this.isCentralTeam() && this.isTechEx();
        }
        return false;
    }

    canEditComment(segment: SegmentWithPermissions, account: AccountDataWithChecks, comment: CellComment) {
        if (this.isSuperuser()) return true;
        return this.canDeleteComment(segment, account, comment);
    }
}
